import { type ComponentProps, type ReactNode, useState } from "react";
import { Dialog, DialogContent, DialogTrigger, useUncontrolledState } from "@tellescope/ui";
import { EventDetailsDialogContents } from "./EventDetailsDialogContents";
import { EventFormDialogContents } from "./EventFormDialogContent/EventFormDialogContents";

export interface EventDialogProps extends Omit<ComponentProps<typeof Dialog>, "children"> {
  children?: ReactNode;
  eventId?: string;
  onCopyId?: () => void;
  onEnduserClick: (enduserId: string) => void;
  timezone?: string;
  onSuccess?: () => void;
}

export const EventDialog = ({
  children,
  eventId,
  open: providedOpen,
  onOpenChange: providedOnOpenChange,
  onCopyId,
  timezone,
  onEnduserClick,
  defaultOpen,
  onSuccess,
  ...rest
}: EventDialogProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [open, setOpen] = useUncontrolledState({
    state: providedOpen ?? defaultOpen ?? false,
    setState: (val) => {
      const newVal = typeof val === "function" ? val(providedOpen ?? defaultOpen ?? false) : val;
      providedOnOpenChange?.(newVal);
    },
  });

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) setIsEditing(false);
    setOpen(newOpen);
  };

  return (
    <Dialog {...rest} open={open} onOpenChange={handleOpenChange}>
      {!!children && <DialogTrigger>{children}</DialogTrigger>}
      <DialogContent
        showCloseButton={false}
        divided
        overlayVariant="light"
        className="font-light transition-all overflow-hidden max-h-[90svh] flex flex-col"
      >
        {!eventId || isEditing ? (
          <EventFormDialogContents
            eventId={eventId}
            onSuccess={() => {
              onSuccess?.();
              setIsEditing(false);
            }}
            onCancel={() => {
              setIsEditing(false);
            }}
            timezone={timezone}
          />
        ) : (
          <EventDetailsDialogContents
            eventId={eventId}
            onEnduserClick={onEnduserClick}
            onEditClick={() => setIsEditing(true)}
            onCopyId={onCopyId}
            onDeleteSuccess={() => {
              onSuccess?.();
              handleOpenChange(false);
            }}
            timezone={timezone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
